import React, { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Textarea } from "../../ui/textarea";
import { Checkbox } from "../../ui/checkbox";
import { Badge } from "../../ui/badge";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Alert, AlertDescription } from "../../ui/alert";
import { Tabs, TabsList, TabsTrigger } from "../../ui/tabs";
import {
  Sparkles,
  Lightbulb,
  Loader2,
  Check,
  Edit2,
  Trash2,
  Mic,
  MicOff,
  FileText,
  Upload,
  AlertCircle,
  RefreshCw,
  CheckSquare,
  Calendar,
  Flag,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import {
  UnifiedExtractorDialogProps,
  ExtractionType,
  ExtractedItem,
  AIExtractedInsight,
  AIExtractedActionItem,
} from "./types";
import { cn } from "../../../lib/utils";
import useRetryFetch from "../../../hooks/useRetryFetch";
import { useSpeechInput } from "../../../hooks/useSpeechInput";
import { extractItems } from "../../../api/extractAI";
import { GeneratingLoader } from "../../ui/generating-loader";
import i18n from "i18next";
import mammoth from "mammoth";

// Extend Window for Web Speech API
declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

const UnifiedExtractorDialog: React.FC<UnifiedExtractorDialogProps> = ({
  open,
  onOpenChange,
  onExtractComplete,
  categories,
  actionCategories,
  defaultExtractionTypes = ["insight", "action"],
  mode = "both",
  initialText,
}) => {
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const hasAutoTriggered = useRef(false);

  // State
  const [inputText, setInputText] = useState("");
  const [extractionTypes, setExtractionTypes] = useState<ExtractionType[]>(
    mode === "insights" ? ["insight"] : mode === "actions" ? ["action"] : defaultExtractionTypes
  );
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractedItems, setExtractedItems] = useState<ExtractedItem[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [useLocalFallback, setUseLocalFallback] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  // Voice input state — recognition lives in useSpeechInput (spec 2026-11-04 §4)
  const speech = useSpeechInput({
    onText: setInputText,
    onError: () => toast.error(t("voice_error", "Voice input didn't work. Try again.")),
  });
  const isListening = speech.listening;
  const speechSupported = speech.supported;

  // File upload ref
  const fileInputRef = useRef<HTMLInputElement>(null);

  const insightCategories = categories;
  const actionCats = actionCategories || categories;

  // Auto-trigger extraction when dialog opens with initialText
  const [pendingAutoExtract, setPendingAutoExtract] = useState(false);

  useEffect(() => {
    if (open && initialText && !hasAutoTriggered.current) {
      setInputText(initialText);
      hasAutoTriggered.current = true;
      setPendingAutoExtract(true);
    }
    if (!open) {
      hasAutoTriggered.current = false;
    }
  }, [open, initialText]);

  useEffect(() => {
    if (pendingAutoExtract && inputText) {
      setPendingAutoExtract(false);
      handleExtract();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAutoExtract, inputText]);

  // Toggle extraction type
  const toggleExtractionType = (type: ExtractionType) => {
    setExtractionTypes((prev) => {
      if (prev.includes(type)) {
        // Don't allow deselecting if it's the only one
        if (prev.length === 1) return prev;
        return prev.filter((t) => t !== type);
      }
      return [...prev, type];
    });
  };

  // Extract items
  const handleExtract = async () => {
    if (!inputText.trim()) return;

    setIsExtracting(true);
    setExtractedItems([]);
    setError(null);

    try {
      // Try unified API for both insights and actions
      let apiItems: ExtractedItem[] = [];
      try {
        const response = await extractItems(retryFetch, {
          text: inputText,
          extractTypes: extractionTypes,
          language: i18n.language,
        });

        if (response.status === "SUCCESS") {
          // Map API insights
          if (response?.insights && response?.insights?.length > 0) {
            const insights = response?.insights?.map((insight, index) => ({
              id: `insight-${index}-${Date.now()}`,
              type: "insight" as const,
              content: insight.content,
              confidence: insight.confidence,
              suggestedCategory: insight.suggestedCategory || suggestCategory(insight.content, insightCategories),
              suggestedTags: insight.suggestedTags || [],
              selected: true,
            }));
            apiItems = [...apiItems, ...insights];
          }

          // Map API action items
          if (response.actionItems && response.actionItems.length > 0) {
            const actions = response.actionItems.map((action, index) => ({
              id: `action-${index}-${Date.now()}`,
              type: "action" as const,
              content: action.content,
              confidence: action.confidence,
              suggestedCategory: action.suggestedCategory || suggestCategory(action.content, actionCats),
              suggestedPriority: action.suggestedPriority,
              suggestedDueDate: action.suggestedDueDate,
              dueDateLabel: action.dueDateLabel,
              selected: true,
            } as AIExtractedActionItem));
            apiItems = [...apiItems, ...actions];
          }
        }
      } catch (err) {
        console.log("API extraction failed, will use local fallback");
      }

      // Local extraction for both types as fallback
      const localItems = extractItemsLocally(inputText, extractionTypes);

      // Merge: prefer API items, add local ones only if API didn't return any
      let allItems: ExtractedItem[] = [...apiItems];

      // Add local items only if API didn't return any
      if (apiItems.length === 0) {
        allItems = [...localItems];
      }

      if (allItems.length > 0) {
        setExtractedItems(allItems);
        setUseLocalFallback(apiItems.length === 0);
      } else {
        setError(t("no_items_found") || "No insights or action items could be extracted. Try adding more detail.");
      }
    } catch (err) {
      console.error("Extraction failed:", err);
      // Full fallback
      const localItems = extractItemsLocally(inputText, extractionTypes);
      if (localItems.length > 0) {
        setExtractedItems(localItems);
        setUseLocalFallback(true);
      } else {
        setError(t("extraction_failed") || "Failed to extract items. Please try again.");
      }
    } finally {
      setIsExtracting(false);
    }
  };

  // Local extraction with type awareness
  const extractItemsLocally = (text: string, types: ExtractionType[]): ExtractedItem[] => {
    const sentences = text
      .split(/(?<=[.!?])\s+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 15);

    const items: ExtractedItem[] = [];

    // Keywords for insights
    const insightKeywords = [
      "important", "key", "crucial", "learned", "realized", "discovered",
      "insight", "takeaway", "lesson", "always", "never", "should",
      "best practice", "remember", "principle", "truth",
    ];

    // Keywords for action items
    const actionKeywords = [
      "need to", "have to", "must", "should", "will", "going to",
      "plan to", "want to", "todo", "action", "task", "schedule",
      "follow up", "reach out", "complete", "finish", "start",
      "call", "email", "meet", "review", "prepare", "create",
      "deadline", "by monday", "by friday", "next week", "tomorrow",
      "asap", "urgent", "priority",
    ];

    // Priority indicators
    const highPriorityIndicators = ["urgent", "asap", "critical", "immediately", "high priority", "must"];
    const lowPriorityIndicators = ["when possible", "eventually", "low priority", "nice to have"];

    // Due date indicators
    const dueDatePatterns: { pattern: RegExp; label: string }[] = [
      { pattern: /\btoday\b/i, label: "Today" },
      { pattern: /\btomorrow\b/i, label: "Tomorrow" },
      { pattern: /\bnext week\b/i, label: "Next week" },
      { pattern: /\bthis week\b/i, label: "This week" },
      { pattern: /\bby (monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/i, label: "This $1" },
      { pattern: /\bnext (monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/i, label: "Next $1" },
      { pattern: /\bend of (week|month|day)\b/i, label: "End of $1" },
      { pattern: /\basap\b/i, label: "ASAP" },
    ];

    sentences.forEach((sentence, index) => {
      const lower = sentence.toLowerCase();
      let insightScore = 0;
      let actionScore = 0;

      // Score for insights
      insightKeywords.forEach((kw) => {
        if (lower.includes(kw)) insightScore += 2;
      });
      if (sentence.includes('"') || sentence.includes("'")) insightScore += 1.5;
      if (sentence.length > 80) insightScore += 0.5;

      // Score for actions
      actionKeywords.forEach((kw) => {
        if (lower.includes(kw)) actionScore += 2.5;
      });
      // Imperative sentences (start with verb)
      if (/^(call|email|meet|review|schedule|prepare|create|send|check|update|follow|reach|complete|finish|start|make|set|get|ask|tell|do|write|read|research|analyze)/i.test(sentence)) {
        actionScore += 3;
      }

      // Determine priority
      let suggestedPriority: "high" | "medium" | "low" | undefined;
      if (highPriorityIndicators.some((p) => lower.includes(p))) {
        suggestedPriority = "high";
      } else if (lowPriorityIndicators.some((p) => lower.includes(p))) {
        suggestedPriority = "low";
      } else if (actionScore > 3) {
        suggestedPriority = "medium";
      }

      // Determine due date
      let dueDateLabel: string | undefined;
      for (const { pattern, label } of dueDatePatterns) {
        const match = lower.match(pattern);
        if (match) {
          dueDateLabel = label.replace("$1", match[1] ? match[1].charAt(0).toUpperCase() + match[1].slice(1) : "");
          break;
        }
      }

      // Create items based on scores
      if (types.includes("action") && actionScore >= 2.5) {
        items.push({
          id: `action-${index}-${Date.now()}`,
          type: "action",
          content: sentence.charAt(0).toUpperCase() + sentence.slice(1),
          confidence: Math.min(0.95, 0.55 + actionScore / 12),
          suggestedCategory: suggestCategory(sentence, actionCats),
          selected: true,
          suggestedPriority,
          dueDateLabel,
        } as AIExtractedActionItem);
      } else if (types.includes("insight") && insightScore >= 2) {
        items.push({
          id: `insight-${index}-${Date.now()}`,
          type: "insight",
          content: sentence.charAt(0).toUpperCase() + sentence.slice(1),
          confidence: Math.min(0.95, 0.55 + insightScore / 10),
          suggestedCategory: suggestCategory(sentence, insightCategories),
          selected: true,
        } as AIExtractedInsight);
      }
    });

    // Sort by confidence and limit
    return items
      .sort((a, b) => b.confidence - a.confidence)
      .slice(0, 10);
  };

  // Suggest category based on content
  const suggestCategory = (content: string, cats: typeof categories): string | undefined => {
    const lower = content.toLowerCase();

    for (const category of cats) {
      if (lower.includes(category.name.toLowerCase())) {
        return category._id;
      }
    }

    const categoryKeywords: Record<string, string[]> = {
      leadership: ["leader", "team", "manage", "delegate", "mentor"],
      communication: ["communicate", "listen", "speak", "feedback", "conversation"],
      growth: ["grow", "learn", "develop", "improve", "progress"],
      strategy: ["strategy", "plan", "goal", "objective", "vision"],
      meetings: ["meet", "meeting", "call", "sync", "standup"],
      finance: ["budget", "cost", "revenue", "expense", "financial"],
      personal: ["personal", "self", "life", "health", "family"],
    };

    for (const [categoryName, keywords] of Object.entries(categoryKeywords)) {
      if (keywords.some((kw) => lower.includes(kw))) {
        const matching = cats.find((c) => c.name.toLowerCase().includes(categoryName));
        if (matching) return matching._id;
      }
    }

    return undefined;
  };

  // Item actions
  const toggleItemSelection = (id: string) => {
    setExtractedItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, selected: !item.selected } : item))
    );
  };

  const startEditing = (id: string, content: string) => {
    setEditingId(id);
    setEditText(content);
  };

  const saveEdit = () => {
    if (!editingId) return;
    setExtractedItems((prev) =>
      prev.map((item) => (item.id === editingId ? { ...item, content: editText } : item))
    );
    setEditingId(null);
    setEditText("");
  };

  const removeItem = (id: string) => {
    setExtractedItems((prev) => prev.filter((item) => item.id !== id));
  };

  const updateItemCategory = (id: string, categoryId: string) => {
    setExtractedItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, suggestedCategory: categoryId === "none" ? undefined : categoryId } : item
      )
    );
  };

  const updateActionPriority = (id: string, priority: "high" | "medium" | "low") => {
    setExtractedItems((prev) =>
      prev.map((item) =>
        item.id === id && item.type === "action"
          ? { ...item, suggestedPriority: priority } as AIExtractedActionItem
          : item
      )
    );
  };

  // Select/deselect by type
  const selectAllOfType = (type: ExtractionType) => {
    setExtractedItems((prev) =>
      prev.map((item) => (item.type === type ? { ...item, selected: true } : item))
    );
  };

  const deselectAllOfType = (type: ExtractionType) => {
    setExtractedItems((prev) =>
      prev.map((item) => (item.type === type ? { ...item, selected: false } : item))
    );
  };

  const toggleSection = (section: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  // Submit
  const handleAddSelected = () => {
    const selected = extractedItems.filter((i) => i.selected);
    onExtractComplete(selected);
    handleClose();
  };

  const handleClose = () => {
    // Stop voice recognition if active
    if (speech.listening) speech.stop();
    setInputText("");
    setExtractedItems([]);
    setIsExtracting(false);
    setEditingId(null);
    setError(null);
    onOpenChange(false);
  };

  const handleRetry = () => {
    setError(null);
    setExtractedItems([]);
  };

  // Voice input handler
  const handleVoiceInput = () => {
    if (speech.listening) speech.stop();
    else speech.start(inputText);
  };

  // File upload handler
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      if (file.name.endsWith(".txt")) {
        const text = await file.text();
        setInputText(text);
        toast.success(t("file_loaded", "Loaded: {{name}}", { name: file.name }));
      } else if (file.name.endsWith(".docx")) {
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });
        setInputText(result.value);
        toast.success(t("file_loaded", "Loaded: {{name}}", { name: file.name }));
      } else {
        toast.error(t("unsupported_file", "Unsupported file type. Use .txt or .docx"));
      }
    } catch (err) {
      console.error("File upload error:", err);
      toast.error(t("file_upload_error", "Failed to read file"));
    }

    // Reset input so re-uploading the same file works
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Computed values
  const insights = extractedItems.filter((i) => i.type === "insight");
  const actions = extractedItems.filter((i) => i.type === "action") as AIExtractedActionItem[];
  const selectedCount = extractedItems.filter((i) => i.selected).length;
  const selectedInsights = insights.filter((i) => i.selected).length;
  const selectedActions = actions.filter((i) => i.selected).length;

  const getCategoryById = useCallback(
    (id: string | undefined, type: ExtractionType) => {
      if (!id) return null;
      const cats = type === "insight" ? insightCategories : actionCats;
      return cats.find((c) => c._id === id);
    },
    [insightCategories, actionCats]
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="pb-2">
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-200">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <span className="text-lg">{t("ai_extractor.title", "AI Smart Extractor")}</span>
              <p className="text-sm font-normal text-muted-foreground mt-0.5">
                {t("ai_extractor.subtitle", "Extract insights and action items from your notes")}
              </p>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          {/* Error State */}
          {error && (
            <Alert variant="destructive" className="bg-red-50 border-red-200">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between">
                <span>{error}</span>
                <Button variant="ghost" size="sm" onClick={handleRetry} className="ms-2">
                  <RefreshCw className="h-3 w-3 me-1" />
                  {t("try_again", "Try Again")}
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* Input Section */}
          {extractedItems.length === 0 && !error && (
            <div className="space-y-4">
              {/* Extraction Type Selector */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  {t("ai_extractor.what_to_extract", "What would you like to extract?")}
                </Label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => toggleExtractionType("insight")}
                    className={cn(
                      "flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 transition-all",
                      extractionTypes.includes("insight")
                        ? "border-amber-400 bg-amber-50 text-amber-700 shadow-sm"
                        : "border-gray-200 hover:border-gray-300 text-gray-600"
                    )}
                  >
                    <Lightbulb className={cn("h-4 w-4", extractionTypes.includes("insight") ? "text-amber-500" : "text-gray-400")} />
                    <span className="font-medium text-sm">{t("insights", "Insights")}</span>
                    {extractionTypes.includes("insight") && (
                      <Check className="h-4 w-4 text-amber-600" />
                    )}
                  </button>

                  <button
                    onClick={() => toggleExtractionType("action")}
                    className={cn(
                      "flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 transition-all",
                      extractionTypes.includes("action")
                        ? "border-primary/40 bg-primary/5 text-primary shadow-sm"
                        : "border-gray-200 hover:border-gray-300 text-gray-600"
                    )}
                  >
                    <CheckSquare className={cn("h-4 w-4", extractionTypes.includes("action") ? "text-primary" : "text-gray-400")} />
                    <span className="font-medium text-sm">{t("action_items", "Action Items")}</span>
                    {extractionTypes.includes("action") && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </button>
                </div>
              </div>

              {/* Text Input */}
              <div className="space-y-2">
                <Textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder={t("ai_extractor.placeholder", `Paste your notes, meeting summary, or thoughts here...

Example:
Had a great 1:1 with Sarah. Key insight: leaders ask questions instead of giving answers. I need to schedule follow-up meetings with the team by Friday. Learned that vulnerability builds trust. Todo: review the Q1 budget proposal ASAP. Remember to practice active listening more.`)}
                  className="min-h-[200px] resize-none text-sm border-2 focus:border-violet-300 rounded-xl"
                  disabled={isExtracting}
                />

                {/* Character count & options */}
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "h-7 text-xs",
                        isListening && "text-red-600 bg-red-50",
                        !speechSupported && "opacity-50"
                      )}
                      disabled={!speechSupported || isExtracting}
                      onClick={handleVoiceInput}
                      title={!speechSupported ? t("voice_not_supported", "Not supported in this browser") : undefined}
                    >
                      {isListening ? (
                        <MicOff className="h-3 w-3 me-1" />
                      ) : (
                        <Mic className="h-3 w-3 me-1" />
                      )}
                      {isListening ? t("stop", "Stop") : t("voice", "Voice")}
                      {isListening && <span className="ms-1 w-2 h-2 rounded-full bg-red-500 animate-pulse" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      disabled={isExtracting}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-3 w-3 me-1" />
                      {t("upload", "Upload")}
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".txt,.docx"
                      className="hidden"
                      onChange={handleFileUpload}
                    />
                  </div>
                  <span className={cn(inputText.length > 50 ? "text-green-600" : "text-gray-400")}>
                    {inputText.length} {t("characters", "characters")}
                  </span>
                </div>

                {inputText.length > 0 && inputText.length < 100 && (
                  <p className="text-xs text-amber-600 bg-amber-50 p-2.5 rounded-lg border border-amber-100">
                    💡 {t("ai_extractor.tip", "Add more detail for better results. Include your reflections, action items, and deadlines.")}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Extraction Animation */}
          {isExtracting && (
            <div className="flex items-center justify-center py-16">
              <GeneratingLoader context="extraction" />
            </div>
          )}

          {/* Extracted Results */}
          {extractedItems.length > 0 && !isExtracting && (
            <div className="space-y-4">
              {/* Summary bar */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-gray-50 to-gray-100 border">
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium">
                    {t("ai_extractor.found", "Found")} {extractedItems.length} {t("items", "items")}
                  </span>
                  {useLocalFallback && (
                    <Badge variant="outline" className="text-xs text-amber-600 border-amber-200 bg-amber-50">
                      {t("smart_extract", "Smart Extract")}
                    </Badge>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setExtractedItems([])}
                  className="text-xs text-muted-foreground"
                >
                  {t("try_different", "← Try different text")}
                </Button>
              </div>

              {/* Insights Section */}
              {insights.length > 0 && (
                <div className="border rounded-xl overflow-hidden">
                  <button
                    onClick={() => toggleSection("insights")}
                    className="w-full flex items-center justify-between p-3 bg-amber-50/50 hover:bg-amber-50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Lightbulb className="h-4 w-4 text-amber-500" />
                      <span className="font-medium text-sm text-amber-800">
                        {t("insights", "Insights")}
                      </span>
                      <Badge className="bg-amber-100 text-amber-700 border-0">
                        {insights.length}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          selectedInsights === insights.length
                            ? deselectAllOfType("insight")
                            : selectAllOfType("insight");
                        }}
                      >
                        {selectedInsights === insights.length ? t("deselect_all", "Deselect All") : t("select_all", "Select All")}
                      </Button>
                      {collapsedSections.has("insights") ? (
                        <ChevronDown className="h-4 w-4 text-amber-600" />
                      ) : (
                        <ChevronUp className="h-4 w-4 text-amber-600" />
                      )}
                    </div>
                  </button>

                  {!collapsedSections.has("insights") && (
                    <div className="divide-y">
                      {insights.map((item) => (
                        <ExtractedItemRow
                          key={item.id}
                          item={item}
                          category={getCategoryById(item.suggestedCategory, "insight")}
                          categories={insightCategories}
                          isEditing={editingId === item.id}
                          editText={editText}
                          onEditTextChange={setEditText}
                          onToggleSelect={() => toggleItemSelection(item.id)}
                          onStartEdit={() => startEditing(item.id, item.content)}
                          onSaveEdit={saveEdit}
                          onCancelEdit={() => setEditingId(null)}
                          onRemove={() => removeItem(item.id)}
                          onUpdateCategory={(catId) => updateItemCategory(item.id, catId)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Action Items Section */}
              {actions.length > 0 && (
                <div className="border rounded-xl overflow-hidden">
                  <button
                    onClick={() => toggleSection("actions")}
                    className="w-full flex items-center justify-between p-3 bg-primary/5/50 hover:bg-primary/5 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <CheckSquare className="h-4 w-4 text-primary" />
                      <span className="font-medium text-sm text-primary">
                        {t("action_items", "Action Items")}
                      </span>
                      <Badge className="bg-primary/10 text-primary border-0">
                        {actions.length}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          selectedActions === actions.length
                            ? deselectAllOfType("action")
                            : selectAllOfType("action");
                        }}
                      >
                        {selectedActions === actions.length ? t("deselect_all", "Deselect All") : t("select_all", "Select All")}
                      </Button>
                      {collapsedSections.has("actions") ? (
                        <ChevronDown className="h-4 w-4 text-primary" />
                      ) : (
                        <ChevronUp className="h-4 w-4 text-primary" />
                      )}
                    </div>
                  </button>

                  {!collapsedSections.has("actions") && (
                    <div className="divide-y">
                      {actions.map((item) => (
                        <ExtractedActionItemRow
                          key={item.id}
                          item={item}
                          category={getCategoryById(item.suggestedCategory, "action")}
                          categories={actionCats}
                          isEditing={editingId === item.id}
                          editText={editText}
                          onEditTextChange={setEditText}
                          onToggleSelect={() => toggleItemSelection(item.id)}
                          onStartEdit={() => startEditing(item.id, item.content)}
                          onSaveEdit={saveEdit}
                          onCancelEdit={() => setEditingId(null)}
                          onRemove={() => removeItem(item.id)}
                          onUpdateCategory={(catId) => updateItemCategory(item.id, catId)}
                          onUpdatePriority={(p) => updateActionPriority(item.id, p)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0 border-t pt-4">
          <Button variant="outline" onClick={handleClose}>
            {t("cancel", "Cancel")}
          </Button>

          {extractedItems.length === 0 ? (
            <Button
              onClick={handleExtract}
              disabled={!inputText.trim() || inputText.length < 20 || isExtracting || extractionTypes.length === 0}
              className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 shadow-lg shadow-violet-200"
            >
              {isExtracting ? (
                <>
                  <Loader2 className="h-4 w-4 me-2 animate-spin" />
                  {t("extracting", "Extracting...")}
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 me-2" />
                  {t("extract", "Extract with AI")}
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={handleAddSelected}
              disabled={selectedCount === 0}
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 shadow-lg shadow-green-200"
            >
              <Check className="h-4 w-4 me-2" />
              {t("add_selected", "Add Selected")} ({selectedCount})
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Insight item row component
interface ExtractedItemRowProps {
  item: ExtractedItem;
  category: { _id: string; name: string; color: string } | null | undefined;
  categories: { _id: string; name: string; color: string }[];
  isEditing: boolean;
  editText: string;
  onEditTextChange: (text: string) => void;
  onToggleSelect: () => void;
  onStartEdit: () => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onRemove: () => void;
  onUpdateCategory: (categoryId: string) => void;
}

const ExtractedItemRow: React.FC<ExtractedItemRowProps> = ({
  item,
  category,
  categories,
  isEditing,
  editText,
  onEditTextChange,
  onToggleSelect,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onRemove,
  onUpdateCategory,
}) => {
  const { t } = useTranslation();

  // Check if suggestedCategory is a name (not an ID)
  const isNewCategory = item.suggestedCategory && !categories.some(c => c._id === item.suggestedCategory);
  const categoryName = isNewCategory ? item.suggestedCategory : undefined;

  if (isEditing) {
    return (
      <div className="p-3 bg-gray-50 space-y-2">
        <Textarea
          value={editText}
          onChange={(e) => onEditTextChange(e.target.value)}
          className="min-h-[60px] text-sm"
          autoFocus
        />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onCancelEdit}>
            {t("cancel", "Cancel")}
          </Button>
          <Button size="sm" onClick={onSaveEdit}>
            {t("save", "Save")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("p-3 transition-all", item.selected ? "bg-white" : "bg-gray-50/50 opacity-60")}>
      <div className="flex items-start gap-3">
        <Checkbox
          checked={item.selected}
          onCheckedChange={onToggleSelect}
          className="mt-0.5 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-700 leading-relaxed">{item.content}</p>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <Select value={item.suggestedCategory || "none"} onValueChange={onUpdateCategory}>
              <SelectTrigger className={cn(
                "h-7 w-[160px] text-xs",
                isNewCategory && "bg-gradient-to-r from-violet-50 to-purple-50 border-violet-300"
              )}>
                <SelectValue placeholder={t("category", "Category")}>
                  {isNewCategory ? (
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="h-3 w-3 text-violet-600" />
                      <span className="font-semibold text-violet-700">{categoryName}</span>
                    </div>
                  ) : item.suggestedCategory ? (
                    <span>{categories.find(c => c._id === item.suggestedCategory)?.name}</span>
                  ) : (
                    <span>{t("category", "Category")}</span>
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {isNewCategory && (
                  <>
                    <SelectItem value={item.suggestedCategory!}>
                      <div className="flex items-center gap-1.5">
                        <Sparkles className="h-3 w-3 text-violet-600" />
                        <span className="font-semibold">{categoryName}</span>
                        <Badge variant="outline" className="ms-1 text-[9px] px-1.5 py-0 h-4 border-violet-200 text-violet-600">
                          {t("ai_extractor.new_category", "New")}
                        </Badge>
                      </div>
                    </SelectItem>
                    <div className="h-px bg-gray-200 my-1" />
                  </>
                )}
                <SelectItem value="none">{t("no_category", "No Category")}</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat._id} value={cat._id}>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                      {cat.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Badge
              variant="outline"
              className={cn(
                "text-xs",
                item.confidence >= 0.8
                  ? "border-green-200 text-green-700 bg-green-50"
                  : item.confidence >= 0.6
                  ? "border-amber-200 text-amber-700 bg-amber-50"
                  : "border-gray-200 text-gray-600 bg-gray-50"
              )}
            >
              {Math.round(item.confidence * 100)}%
            </Badge>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onStartEdit}>
            <Edit2 className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-red-50"
            onClick={onRemove}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Action item row with priority and due date
interface ExtractedActionItemRowProps extends ExtractedItemRowProps {
  item: AIExtractedActionItem;
  onUpdatePriority: (priority: "high" | "medium" | "low") => void;
}

const ExtractedActionItemRow: React.FC<ExtractedActionItemRowProps> = ({
  item,
  category,
  categories,
  isEditing,
  editText,
  onEditTextChange,
  onToggleSelect,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onRemove,
  onUpdateCategory,
  onUpdatePriority,
}) => {
  const { t } = useTranslation();

  // Check if suggestedCategory is a name (not an ID)
  const isNewCategory = item.suggestedCategory && !categories.some(c => c._id === item.suggestedCategory);
  const categoryName = isNewCategory ? item.suggestedCategory : undefined;

  if (isEditing) {
    return (
      <div className="p-3 bg-gray-50 space-y-2">
        <Textarea
          value={editText}
          onChange={(e) => onEditTextChange(e.target.value)}
          className="min-h-[60px] text-sm"
          autoFocus
        />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onCancelEdit}>
            {t("cancel", "Cancel")}
          </Button>
          <Button size="sm" onClick={onSaveEdit}>
            {t("save", "Save")}
          </Button>
        </div>
      </div>
    );
  }

  const priorityColors = {
    high: "border-red-200 text-red-700 bg-red-50",
    medium: "border-amber-200 text-amber-700 bg-amber-50",
    low: "border-green-200 text-green-700 bg-green-50",
  };

  return (
    <div className={cn("p-3 transition-all", item.selected ? "bg-white" : "bg-gray-50/50 opacity-60")}>
      <div className="flex items-start gap-3">
        <Checkbox
          checked={item.selected}
          onCheckedChange={onToggleSelect}
          className="mt-0.5 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-700 leading-relaxed">{item.content}</p>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {/* Category */}
            <Select value={item.suggestedCategory || "none"} onValueChange={onUpdateCategory}>
              <SelectTrigger className={cn(
                "h-7 w-[140px] text-xs",
                isNewCategory && "bg-gradient-to-r from-violet-50 to-purple-50 border-violet-300"
              )}>
                <SelectValue placeholder={t("category", "Category")}>
                  {isNewCategory ? (
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="h-3 w-3 text-violet-600" />
                      <span className="font-semibold text-violet-700">{categoryName}</span>
                    </div>
                  ) : item.suggestedCategory ? (
                    <span>{categories.find(c => c._id === item.suggestedCategory)?.name}</span>
                  ) : (
                    <span>{t("category", "Category")}</span>
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {isNewCategory && (
                  <>
                    <SelectItem value={item.suggestedCategory!}>
                      <div className="flex items-center gap-1.5">
                        <Sparkles className="h-3 w-3 text-violet-600" />
                        <span className="font-semibold">{categoryName}</span>
                        <Badge variant="outline" className="ms-1 text-[9px] px-1.5 py-0 h-4 border-violet-200 text-violet-600">
                          {t("ai_extractor.new_category", "New")}
                        </Badge>
                      </div>
                    </SelectItem>
                    <div className="h-px bg-gray-200 my-1" />
                  </>
                )}
                <SelectItem value="none">{t("no_category", "No Category")}</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat._id} value={cat._id}>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                      {cat.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Priority */}
            <Select value={item.suggestedPriority || "medium"} onValueChange={(v) => onUpdatePriority(v as "high" | "medium" | "low")}>
              <SelectTrigger className="h-7 w-[100px] text-xs">
                <Flag className="h-3 w-3 me-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="high">
                  <span className="text-red-600">{t("high", "High")}</span>
                </SelectItem>
                <SelectItem value="medium">
                  <span className="text-amber-600">{t("medium", "Medium")}</span>
                </SelectItem>
                <SelectItem value="low">
                  <span className="text-green-600">{t("low", "Low")}</span>
                </SelectItem>
              </SelectContent>
            </Select>

            {/* Due date label if detected */}
            {item.dueDateLabel && (
              <Badge variant="outline" className="text-xs border-primary/20 text-primary bg-primary/5">
                <Calendar className="h-3 w-3 me-1" />
                {item.dueDateLabel}
              </Badge>
            )}

            {/* Confidence */}
            <Badge
              variant="outline"
              className={cn(
                "text-xs",
                item.confidence >= 0.8
                  ? "border-green-200 text-green-700 bg-green-50"
                  : item.confidence >= 0.6
                  ? "border-amber-200 text-amber-700 bg-amber-50"
                  : "border-gray-200 text-gray-600 bg-gray-50"
              )}
            >
              {Math.round(item.confidence * 100)}%
            </Badge>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onStartEdit}>
            <Edit2 className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-red-50"
            onClick={onRemove}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UnifiedExtractorDialog;
